import type { AuditAnswer, SnapshotQuestion } from '../api/audits';

/**
 * Whether a question is finished, and what it is still short of.
 *
 * <p><b>One definition, in one place.</b> The screen counted a question as done when it had
 * an answer, and the question card worked out separately that the answer also owed a
 * comment or a photograph. Neither told the other, so the header could read 6/6 while the
 * server refused the submission — the counter said finished and the rules said otherwise,
 * and the auditor had no way to find out which question disagreed.</p>
 *
 * <p>Pure, and tested, because it is the thing an auditor standing in a store trusts to
 * tell them whether they can leave.</p>
 */

/** What a question still needs, as the screen draws it. */
export type Completeness =
  /** Nothing has been entered. */
  | 'empty'
  /** Answered, and still owes a comment or a photograph the form demands. */
  | 'owing'
  /** Not applicable — by the auditor's choice, or because the form skipped it. */
  | 'skipped'
  /** Done. */
  | 'done';

/** What is left: shown beside the question so the demand is specific. */
export interface Owed {
  comment: boolean;
  photo: boolean;
}

/** Whether anything at all has been entered. */
export function isAnswered(answer: AuditAnswer | undefined): boolean {
  if (!answer) return false;

  return (
    answer.yesNo != null ||
    answer.scaleValue != null ||
    answer.selectedOptionIds.length > 0 ||
    (answer.text?.trim() ?? '') !== ''
  );
}

export function isSkipped(answer: AuditAnswer | undefined): boolean {
  return Boolean(answer?.markedNotApplicable || answer?.structurallyNotApplicable);
}

/**
 * The share of the available points a choice answer earned, 0 to 1.
 *
 * <p>Used to decide whether an answer counts as negative, which is what turns a
 * "when negative" requirement on.</p>
 */
export function earned(question: SnapshotQuestion, answer: AuditAnswer | undefined): number {
  const chosen = answer?.selectedOptionIds ?? [];
  const options = question.options ?? [];

  if (question.type === 'SingleChoice') {
    const picked = options.find((o) => chosen.includes(o.id));
    return picked ? picked.score / 100 : 0;
  }

  const total = options.reduce((sum, o) => sum + o.score, 0);
  if (total <= 0) return 0;

  return options.filter((o) => chosen.includes(o.id)).reduce((sum, o) => sum + o.score, 0) / total;
}

/**
 * Whether the answer is the one that makes the form ask for more.
 *
 * <p>A scale counts as negative below its midpoint, which is the same line the scoring
 * engine draws. A choice counts as negative when it earned less than everything available:
 * anything short of full marks is a finding.</p>
 */
export function isNegative(question: SnapshotQuestion, answer: AuditAnswer | undefined): boolean {
  switch (question.type) {
    case 'YesNo':
      return answer?.yesNo === false;

    case 'Scale':
      return (
        answer?.scaleValue != null &&
        answer.scaleValue < ((question.scale?.min ?? 0) + (question.scale?.max ?? 10)) / 2
      );

    case 'SingleChoice':
    case 'MultipleChoice':
      return (answer?.selectedOptionIds.length ?? 0) > 0 && earned(question, answer) < 1;

    default:
      return false;
  }
}

/**
 * What this question still owes.
 *
 * <p>Nothing, while it is skipped or unanswered: a demand for a photograph of an answer
 * nobody has given yet is noise, and the auditor is told the moment they answer.</p>
 */
export function owed(question: SnapshotQuestion, answer: AuditAnswer | undefined): Owed {
  if (isSkipped(answer) || !isAnswered(answer)) return { comment: false, photo: false };

  const negative = isNegative(question, answer);
  const due = (trigger: SnapshotQuestion['comment']) =>
    trigger === 'Always' || (trigger === 'WhenNegative' && negative);

  return {
    comment: due(question.comment) && (answer?.comment?.trim() ?? '') === '',
    photo: due(question.photo) && (answer?.photos.length ?? 0) === 0,
  };
}

/** Whether the visit has to be signed for, and whether it has been. */
export interface Signature {
  required: boolean;
  given: boolean;
}

/** Where this question stands. */
export function completeness(
  question: SnapshotQuestion,
  answer: AuditAnswer | undefined,
): Completeness {
  if (isSkipped(answer)) return 'skipped';
  if (!isAnswered(answer)) return 'empty';

  const still = owed(question, answer);
  return still.comment || still.photo ? 'owing' : 'done';
}

/** Whether this question is finished, by the same rule the server submits by. */
export function isComplete(
  question: SnapshotQuestion,
  answer: AuditAnswer | undefined,
): boolean {
  const where = completeness(question, answer);
  return where === 'done' || where === 'skipped';
}

/**
 * How many of these are finished.
 *
 * <p>Takes the questions already filtered to the ones on screen: a question hidden by a
 * condition is not owed and must not count against the total, or a visit could never read
 * as finished.</p>
 */
export function progress(
  questions: SnapshotQuestion[],
  answers: Map<string, AuditAnswer>,
  signature: Signature = { required: false, given: false },
): { done: number; total: number } {
  const done = questions.filter((q) => isComplete(q, answers.get(q.id))).length;

  // The signature counts, when the form asks for one. Left out, the header reads 6/6 on a
  // visit nobody has signed and the server refuses it — the same two definitions of
  // finished that this module exists to collapse into one, in the one place that is not a
  // question.
  if (!signature.required) return { done, total: questions.length };

  return {
    done: done + (signature.given ? 1 : 0),
    total: questions.length + 1,
  };
}
